import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import type { Socket } from 'socket.io-client'
import { getSocket, disconnectSocket } from './socketClient'

const SocketContext = createContext<Socket | null>(null)

/** Verbindet erst, wenn ein authentifizierter Bereich (AppShell) gemountet wird. */
export function SocketProvider({ children }: { children: ReactNode }) {
  const [socket, setSocket] = useState<Socket | null>(null)

  // Verbindung wird bewusst HIER im Effekt aufgebaut, nicht über einen useState-Lazy-
  // Initializer — React 18s StrictMode führt Effekte im Dev-Modus zu Testzwecken doppelt
  // aus (mount → cleanup → mount). Mit einem Lazy-Initializer würde nur der ERSTE Mount
  // je einen Socket erzeugen; das anschließende Cleanup trennt ihn (disconnect() schließt
  // Auto-Reconnect bei socket.io-client bewusst aus), der zweite Mount-Durchlauf erzeugt
  // aber NIE einen neuen — der Context hält dauerhaft eine tote Referenz, obwohl die
  // Komponente sichtbar weiter gemountet ist. Liegt der getSocket()-Aufruf stattdessen im
  // Effekt selbst, erzeugt jeder Mount-Durchlauf (auch der zweite von StrictMode) seine
  // eigene frische, verbundene Instanz — in Produktion (kein Doppel-Invoke) identisches
  // Verhalten wie vorher.
  useEffect(() => {
    setSocket(getSocket())
    return () => {
      disconnectSocket()
      setSocket(null)
    }
  }, [])

  return <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>
}

export function useSocket(): Socket | null {
  return useContext(SocketContext)
}
